import React, { useState, useEffect, useMemo } from 'react';
import InspectionLayout from '../InspectionLayout';
import NuDate from '../../../../Components/Utils/NuDate';
import { Progress, Select, Button } from 'antd';
import axiosInstance from '../../../../Services/axiosCommon';
import EditIcon from '../../../../Components/Icons/Edit';
import EditCalendar from './EditCalendar';
import FilesView from '../../../../Components/Utils/FilesView';

const { Option } = Select;

function CalendarIndex({ data, id, getData=()=>{} }) {
  const now = useMemo(() => new Date(), []);
  const [currentDate, setCurrentDate] = useState(now);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [percentOpenTask, setPercentOpenTask] = useState(false);
  const [newCompletion, setNewCompletion] = useState(0);
  const [filePathList, setFilePathList] = useState([]);

  // Get localized date string (YYYY-MM-DD)
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Months and years for selectors
  const months = useMemo(() => [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ], []);

  const years = useMemo(() => 
    Array.from({ length: 10 }, (_, i) => now.getFullYear() - 5 + i), 
    [now]
  );

   const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const monthParam = currentDate.getMonth() + 1;
      const yearParam = currentDate.getFullYear();
      const response = await axiosInstance.get(
        `/calendar/events/${monthParam}/month/${yearParam}/year`
      );
      
      const processedData = response.data.tasks_by_date.map(item => ({
        ...item,
        date: getLocalDateString(new Date(item.date))
      }));

      setCalendarData(processedData);

      // Set initial selected date
      const todayString = getLocalDateString(now);
      const todayData = processedData.find(d => d.date === todayString);
      if (todayData) {
        setSelectedDate({
          date: new Date(now),
          ...todayData
        });
        setSelectedTask(todayData.tasks[0]);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

   const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month
    const prevMonthLastDate = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDate - i),
        currentMonth: false,
        task_count: 0,
        tasks: [],
      });
    }

    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateString = getLocalDateString(date);
      const dayData = calendarData.find(d => d.date === dateString) || { 
        task_count: 0, 
        tasks: [] 
      };
      days.push({ ...dayData, date, currentMonth: true });
    }

    // Next month (ensure 6 weeks)
    const totalCells = 42;
    const remainingCells = totalCells - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        currentMonth: false,
        task_count: 0,
        tasks: [],
      });
    }

    return days;
  }, [calendarData, currentDate]);

  // Date click handler
  const handleDateClick = (day) => {
    setSelectedDate(day);
    if (day.currentMonth && day.task_count > 0) {
      setSelectedTask(day.tasks[0]);
    }
  };

  // Month navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  // Progress circle component
  const renderProgressCircle = (task) => (
    <span 
      className="ml-2 text-gray-700 cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        setSelected(task);
        setNewCompletion(task.taskCompletion || 0);
        setPercentOpenTask(true);
      }}
    >
      {/* <Progress
        type="circle"
        size={45}
        strokeWidth={12}
        percent={Math.floor(task.taskCompletion) || 0}
        strokeColor={
          task.taskCompletion > 95 ? "#87d068" :
          task.taskCompletion > 50 ? "#108ee9" : "#D2BD00"
        }
      /> */}
    </span>
  );

  // Update task completion
  const handleUpdateCompletion = async () => {
    if (!selected || newCompletion < 0 || newCompletion > 100) return;
  
    setLoading(true);
    try {
      await axiosInstance.put(`/calendar/task-completion/${selected.id}`, {
        taskCompletion: newCompletion,
      });

      // Update local state
      setCalendarData(prev => prev.map(day => ({
        ...day,
        tasks: day.tasks?.map(task => 
          task.id === selected.id ? { ...task, taskCompletion: newCompletion } : task
        ) || []
      })));

      setPercentOpenTask(false);
      setSelectedTask(prev => prev?.id === selected.id ? 
        { ...prev, taskCompletion: newCompletion } : prev);
        getData();
        fetchCalendarData();
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file list updates
  useEffect(() => {
    if (data?.attachments) {
      setFilePathList(Array.isArray(data.attachments) ? 
        data.attachments : 
        JSON.parse(data.attachments));
    }
  }, [data]);

  return (
    <div className='mt-2'>
    <InspectionLayout>
    <div className="flex flex-col md:flex-row gap-3 h-[calc(100vh-165px)]">
   <div className="bg-white rounded-lg shadow w-full md:w-[70%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-165px)]">
      <div className="sticky top-0 z-10 rounded-t-lg bg-white p-4 border-b flex justify-between items-center h-14">
      <Button onClick={() => navigateMonth('prev')}>&lt;</Button>
      <div className="flex items-center gap-4">
        <Select
          value={currentDate.getMonth() + 1}
          style={{ width: 120 }}
          onChange={value => {
            const newDate = new Date(currentDate);
            newDate.setMonth(value - 1);
            setCurrentDate(newDate);
          }}
        >
          {months.map(m => (
            <Option key={m.value} value={m.value}>{m.label}</Option>
          ))}
        </Select>
        <Select
          value={currentDate.getFullYear()}
          style={{ width: 100 }}
          onChange={value => {
            const newDate = new Date(currentDate);
            newDate.setFullYear(value);
            setCurrentDate(newDate);
          }}
        >
          {years.map(y => (
            <Option key={y} value={y}>{y}</Option>
          ))}
        </Select>
      </div>
      <Button onClick={() => navigateMonth('next')}>&gt;</Button>
    </div>

      <div className="px-4 flex-1 overflow-y-auto min-h-0">
       <div className="grid grid-cols-7 w-full h-12">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center font-medium text-gray-500 py-2 border">
            {day}
          </div>
        ))}
      </div>

        <div className="grid grid-cols-7 gap-0 w-full">
        {calendarDays.map((day) => {
          const dayDate = new Date(day.date);
          const dateString = getLocalDateString(dayDate);
          const isToday = dateString === getLocalDateString(now);
          const isSelected = selectedDate?.date &&
            dateString === getLocalDateString(new Date(selectedDate.date));
          const hasTasks = day.task_count > 0;

          return (
            <div
              key={dateString}
              onClick={() => handleDateClick(day)}
              className={`p-1 border h-[calc((100vh-273px)/6)] flex flex-col items-center justify-between
                ${day.currentMonth ? "" : "bg-gray-50 text-gray-400"}
                ${isToday ? "bg-yellow-100" : ""}
                ${isSelected ? "bg-blue-50 border-blue-200" : ""}
                cursor-pointer hover:bg-blue-50`}
            >
              <div className="w-full text-right text-sm">
                {dayDate.getDate()}
              </div>
              {hasTasks && (
                <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                  {day.task_count}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>

  {/* Task Details Section */}
  <div className="bg-white rounded-lg shadow w-full md:w-[30%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-165px)]">
    {/* Task Header */}
    <div className="p-4 border-b">
      <p className="text-[#2A475E] text-sm font-semibold">
        {selectedDate ? 
          `Activity for ${new Date(selectedDate.date).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}` : 
          'Select a date'}
      </p>
    </div>

    {/* Scrollable Task List */}
    <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
      {selectedDate?.tasks?.length > 0 ? (
        selectedDate.tasks.map(task => (
          <div
            key={task.id}
            className={`bg-gray-50 rounded-lg p-2 border cursor-pointer
              ${selectedTask?.id === task.id ? "border-blue-500" : "border-gray-200"}`}
            onClick={() => setSelectedTask(task)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
              <h3 className="text-xs font-semibold text-gray-800 line-clamp-2">
  {task.description || "No Description"}
</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <span className="ml-2 text-gray-700">
                      <NuDate value={task.date} format="DD-MMM-YYYY" />
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className="ml-2 text-gray-700">{task.status || "-"}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Assignees:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {/* {task.assignees?.map((assignee, index) => (
                        <span
                          key={index}
                          className="bg-gray-100 rounded-full px-2 py-0.5 text-xs"
                        >
                          {assignee.assigneeName || `None`}
                        </span>
                      ))} */}
                    <span>{task.assignees?.map(i => i.assigneeName).join(", ") || ""}</span>

                    </div>
                  </div>
                  <div className="col-span-2">
                    {task?.attachments?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-700 mb-1">
                          Attachments
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {task.attachments.map((file, index) => (
                            <FilesView key={index} fileView={file} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {renderProgressCircle(task)}
                {/* <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <EditIcon color='blue' height={12} />
                </button> */}
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="flex items-center justify-center h-full text-gray-400">
          Select a date with tasks to view details
        </div>
      )}
    </div>
  </div>
</div>


      {/* Completion Percentage Modal */}
      {percentOpenTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Update Completion</h3>
              <button
                onClick={() => {
                  setPercentOpenTask(false)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <input
              type="number"
              className="w-full p-2 border rounded mb-4"
              value={newCompletion}
              onChange={(e) => {
                const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                setNewCompletion(value);
              }}
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setPercentOpenTask(false)}>Cancel</Button>
              <Button 
                type="primary" 
                onClick={handleUpdateCompletion}
                loading={loading}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {isEditModalOpen && (
        <EditCalendar
          open={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          task={selectedTask}
          id={id}
          fetchData={() => {
            getData();
            fetchCalendarData();
          }}
        />
      )}
    </InspectionLayout>
    </div>
  );
}

export default CalendarIndex;